<template>
  <v-dialog v-model="dialog" max-width="1000px" scrollable>
    <template v-slot:activator="{ on }">
      <div v-if="templateType === 'badge-button'" v-on="!isDisabled && on">
        <mf-button
          outlined
          :badge-content="countSelected"
          label="Segmentos"
          icon="groups"
          iconPosition="left"
          fill-icon
          :disabled="isDisabled"
          text-transform="none"
          class="filter-height"
          size="md"
          :height="40"
        />
      </div>
      <span v-else-if="templateType === 'button'" v-on="!isDisabled && on">
        <mf-button
          :size="btnSmall ? 'sm' : 'md'"
          :disabled="isDisabled"
          :loading="loading"
          data-cy="selectSegments"
          :label="btnText || 'Selecionar segmentos'"
        />
      </span>
      <div v-else-if="templateType === 'input'" v-on="!isDisabled && on">
        <v-text-field :outlined="outlined" hide-details :label="label" :value="textFieldValue" />
      </div>
    </template>
    <v-card class="segmentos-dialog-card">
      <v-card-title>
        <span class="title">Selecionar segmento</span>
      </v-card-title>
      <v-card-subtitle class="py-2">
        {{ getText }}
      </v-card-subtitle>
      <v-card-text class="pa-0">
        <v-row no-gutters>
          <v-col class="pl-6 pr-6 py-2">
            <mf-search v-model.lazy="delaySearch" placeholder="Buscar segmento" hide-details />
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col class="px-6">
            <v-chip-group show-arrows active-class="primary--text">
              <v-chip v-for="(segment, index) in selected" :key="`${segment.name}-${index}`" small :ripple="false" close @click:close="removeSelected(index)">
                <b>{{ segment.name }}</b>
              </v-chip>
            </v-chip-group>
          </v-col>
        </v-row>
        <v-data-table
          v-model="selected"
          :headers="headers"
          :loading="$apollo.queries.segments.loading"
          :options.sync="options"
          :items="items"
          :single-select="singleSelect"
          :server-items-length="records"
          hide-details
          item-key="_id"
          :footer-props="{ itemsPerPageOptions: [5, 10, 15] }"
        >
          <template v-slot:item="props">
            <tr :key="props.item._id" class="cursor-pointer" :active="props.selected" @click="props.selected = !props.selected">
              <td>
                <v-simple-checkbox :value="props.isSelected" color="primary" @input="props.select($event)" />
              </td>
              <td>{{ props.item.name }}</td>
              <td>{{ props.item.description }}</td>
              <td>{{ DateTime.fromISO(props.item.updated_at).toFormat("dd/MM/yyyy '|' HH:mm") }}</td>
              <td>
                <v-progress-circular v-if="props.item.updating" indeterminate color="primary" size="20" />
                <span v-else>{{ props.item.clients_qty | localeString }}</span>
              </td>
              <td>{{ dateToMinutesAgo(props.item.clients_refreshed_at) }}</td>
              <td>
                <mf-button label="Atualizar" icon="cached" outlined size="sm" :disabled="isUpdateDisabled(props.item)" @click="updateSegments(props.item)" />
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
      <mf-action-buttons
        class="pa-2"
        :primary-button="{
          text: 'Selecionar',
          action: save,
          isVisible: true,
          isDisabled: $v.$invalid || disableSave,
          isLoading: loadingSave,
          tooltip: tooltipPrimaryButton,
          tooltipMessage: tooltipPrimaryButtonMessage,
        }"
        :extra-button="{
          text: 'Cancelar',
          action: closeDialog,
          isVisible: true,
        }"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import { cloneDeep } from 'lodash'
import { requiredIf } from 'vuelidate/lib/validators'
import { dateToMinutesAgo } from '@/config/helpers.js'
import { QUERY_GET_SEGMENTS } from '@/modules/campanhas/views/components/graphql'
import { DateTime } from 'luxon'

export default {
  props: {
    selecionados: Array,
    btnSmall: Boolean,
    btnText: String,
    required: {
      type: Boolean,
      default: true,
    },
    disable: Boolean,
    loading: {
      type: Boolean,
      default: false,
    },
    singleLine: {
      type: Boolean,
      default: false,
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    solo: {
      type: Boolean,
      default: false,
    },
    isCampaign: {
      type: Boolean,
      default: false,
    },
    templateType: {
      type: String,
      default: 'button',
    },
    label: {
      type: String,
      default: '',
    },
    singleSelect: Boolean,
    isOfferSchedulePersonalized: {
      type: Boolean,
      default: false,
    },
    disableSave: {
      type: Boolean,
      default: false,
    },
    tooltipPrimaryButton: {
      type: Boolean,
      default: false,
    },
    tooltipPrimaryButtonMessage: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    dialog: false,
    selected: [],
    options: {},
    records: null,
    dateToMinutesAgo: dateToMinutesAgo,
    updating: false,
    items: [],
    searchItem: null,
    delaySearch: null,
    timeout: null,
    DateTime: DateTime,
    loadingSave: false,
  }),
  computed: {
    isDisabled() {
      return this.disable || this.isOfferSchedulePersonalized
    },
    countSelected() {
      return this.selecionados ? this.selecionados.length : 0
    },
    textFieldValue() {
      if (this.selected?.length === 0) return 'Nenhum'
      else if (this.selected?.length === 1) return '1 selecionado'
      else if (this.selected?.length > 1) return `${this.selected.length} selecionados`
      return ''
    },
    getText() {
      return this.singleSelect ? 'Escolha apenas 1 segmento.' : 'Escolha até 3 segmentos.'
    },
    headers() {
      return [
        {
          value: '',
          sortable: false,
        },
        {
          text: 'Título',
          value: 'name',
          width: '25%',
          align: 'left',
        },
        {
          text: 'Descrição',
          value: 'description',
          width: '25%',
          align: 'left',
        },
        {
          text: 'Última alteração',
          value: 'updated_at',
          width: '20%',
          align: 'left',
        },
        {
          text: 'Clientes',
          value: 'clients_qty',
          width: '12%',
          align: 'left',
        },
        {
          text: 'Atualizado',
          value: 'clients_refreshed_at',
          align: 'left',
          width: '15%',
        },
        {
          text: '',
          value: '',
          sortable: false,
        },
      ]
    },
    refreshedSegments() {
      return this.$store.getters.refreshedSegments
    },
    segmentsUpdating() {
      return this.$store.getters.updatingSegmentsIds
    },
  },
  apollo: {
    segments: {
      query: QUERY_GET_SEGMENTS,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          page: this.options.page || 1,
          pageSize: this.options.itemsPerPage || 10,
          search: this.searchItem,
          sort: this.options.sortBy,
          sortDirection: this.options.sortDesc && this.options.sortDesc.map((direction) => (direction ? 'DESC' : 'ASC')),
        }
      },
      update({ segments }) {
        if (!segments) return
        this.records = segments.records
        this.items = segments.segments.map((item) => {
          return {
            ...item,
            updating: false,
          }
        })
      },
    },
  },
  watch: {
    delaySearch(value) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.searchItem = value
        this.options.page = 1
      }, 500)
    },
    dialog(value) {
      if (value) this.selected = this.selecionados
    },
    segmentsUpdating(value) {
      this.updating = value.length > 0
      this.items.forEach((item) => {
        if (value.includes(item._id)) {
          item.updating = true
        } else {
          item.updating = false
        }
      })
    },
    refreshedSegments(refreshedSegments) {
      this.items.forEach((item) => {
        const index = refreshedSegments.findIndex((refreshedSegment) => refreshedSegment._id === item._id)
        if (index > -1) {
          item.clients_refreshed_at = refreshedSegments[index].clients_refreshed_at
          item.clients_qty = refreshedSegments[index].clients_qty
          item.updating = false
        }
      })
      this.updating = false
    },
    selecionados(value) {
      if (value) this.selected = this.selecionados
    },
  },
  mounted() {
    if (this.selecionados?.length) this.selected = this.selecionados
  },
  methods: {
    save() {
      if (this.selected.length > 3) {
        this.$snackbar({ message: 'Selecione no máximo 3 opções.', snackbarColor: 'error' })
        return
      }
      this.$emit('update:selecionados', this.selected)
      this.$emit('change')
      this.dialog = false
    },
    removeSelected(index) {
      const selected = cloneDeep(this.selected)
      selected.splice(index, 1)
      this.selected = selected
    },
    closeDialog() {
      this.options.page = 1
      this.dialog = false
    },
    updateSegments(item) {
      if (item.updating) return
      this.$store.dispatch('updateSegments', [item._id]).catch((error) => {
        console.log(error)
        this.$snackbar({
          message: 'Não foi possível atualizar o segmento, tente novamente mais tarde ou entre em contato com o suporte',
          snackbarColor: 'error',
        })
        this.updating = false
      })
    },
    isUpdateDisabled(item) {
      return (this.updating && !item.updating) || this.disableSave
    },
  },
  validations() {
    return {
      selected: {
        required: requiredIf(function (value) {
          return this.required
        }),
      },
    }
  },
}
</script>
<style lang="scss">
.segmentos-dialog-card {
  .caption {
    opacity: 0.58;
  }

  .btn-update-text {
    margin-right: 8px;
    font-size: 14px;
    letter-spacing: 0.01em;
    text-transform: lowercase;
    &::first-letter {
      text-transform: capitalize;
    }
  }

  .animation {
    animation: rotate 2s infinite reverse;
  }

  @keyframes rotate {
    100% {
      transform: rotate(-360deg);
    }
  }
}
</style>
